'use client';

import { useEffect, useMemo, useState } from 'react';
import { isAppClient } from '@/lib/LocalStorage';
import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage';

const NATIVE_INFO_POPUP_SESSION_KEY = 'native_attribution_info_popup';
const NATIVE_INFO_POPUP_EVENT = 'hzc_native_info_popup';

function stableKeyFromText(text: string): string {
  // best-effort stable key; avoids bringing crypto deps into client bundle
  const s = text.trim().slice(0, 255);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return `native_info_popup_shown_${h}`;
}

export default function NativeAttributionInfoPopup() {
  const [text, setText] = useState<string | null>(null);
  const shownKey = useMemo(() => (text ? stableKeyFromText(text) : null), [text]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAppClient()) return;

    const tryShow = (raw: unknown) => {
      const t = String(raw || '').trim();
      if (!t) return;
      const key = stableKeyFromText(t);
      if (safeLocalStorage.getItem(key) === '1') return;
      safeLocalStorage.setItem(key, '1');
      setText(t);
    };

    const existing = safeSessionStorage.getItem(NATIVE_INFO_POPUP_SESSION_KEY);
    if (existing) tryShow(existing);

    const onPopup = (ev: Event) => {
      const detail = (ev as CustomEvent<{ text?: string }>).detail;
      if (detail?.text) {
        tryShow(detail.text);
      }
    };
    window.addEventListener(NATIVE_INFO_POPUP_EVENT, onPopup);
    return () => window.removeEventListener(NATIVE_INFO_POPUP_EVENT, onPopup);
  }, []);

  if (!text) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Kapat"
        onClick={() => setText(null)}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="p-6 sm:p-7 text-center">
          <div className="text-xl sm:text-2xl font-semibold text-slate-900">{text}</div>
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              className="rounded-xl bg-[#2B3280] px-5 py-2.5 text-sm font-semibold text-white"
              onClick={() => setText(null)}
            >
              Tamam
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
